import React, { Component } from 'react'
import PropTypes from 'prop-types'
import {toastr} from 'react-redux-toastr'
import api from '../../../../../api'
import Loader from '../../../../../shared/components/Loader/Loader.jsx'

import TitleSection from './Components/TitleSection.jsx'
import SummarySection from './Components/SummarySection.jsx'
import MissionsSection from './Components/MissionsSection.jsx'
import DiplomasSection from './Components/DiplomasSection.jsx'
import CertificationsSection from './Components/CertificationsSection.jsx'
import ItSkillsSection from './Components/ItSkillsSection.jsx'
import LanguagesSection from './Components/LanguagesSection.jsx'

import {
  normalizeDiplomas,
  combineAndDeduplicateDiplomas,
  normalizeMissions,
  combineAndDeduplicateMissions,
  normalizeCertifications,
  combineAndDeduplicateCertifications,
  normalizeItSkills,
  combineAndDeduplicateItSkills,
  normalizeLanguages,
  combineAndDeduplicateLanguages
} from './Utils/cvDataHelpers'

export default class CvDataComparisonForm extends Component {
  constructor(props) {
    super(props)

    const { analyse, freelance } = props

    const normalizedAnalyseDiplomas = normalizeDiplomas(analyse.diplomas || [], 'analyse')
    const normalizedFreelanceDiplomas = normalizeDiplomas(freelance.diplomes || [], 'freelance')

    const { combined: combinedDiplomas, initialSelectedDiplomas } = combineAndDeduplicateDiplomas(
      normalizedAnalyseDiplomas,
      normalizedFreelanceDiplomas
    )

    const normalizedAnalyseMissions = normalizeMissions(analyse.missions || [], 'analyse')
    const normalizedFreelanceMissions = normalizeMissions(freelance.experiences || [], 'freelance')

    const { combined: combinedMissions, initialSelectedMissions } = combineAndDeduplicateMissions(
      normalizedAnalyseMissions,
      normalizedFreelanceMissions
    )

    const normalizedAnalyseCertifications = normalizeCertifications(analyse.certifications || [], 'analyse')
    const normalizedFreelanceCertifications = normalizeCertifications(freelance.certifications || [], 'freelance')

    const { combined: combinedCertifications, initialSelectedCertifications } = combineAndDeduplicateCertifications(
      normalizedAnalyseCertifications,
      normalizedFreelanceCertifications
    )

    const normalizedAnalyseItSkills = normalizeItSkills(analyse.itSkills || [], 'analyse')
    const normalizedFreelanceItSkills = normalizeItSkills(freelance.it_skills || [], 'freelance')

    const { combined: combinedItSkills, initialSelectedItSkills } = combineAndDeduplicateItSkills(
      normalizedAnalyseItSkills,
      normalizedFreelanceItSkills
    )

    const normalizedAnalyseLanguages = normalizeLanguages(analyse.languages || [], 'analyse')
    const normalizedFreelanceLanguages = normalizeLanguages(freelance.languages || [], 'freelance')

    const { combined: combinedLanguages, initialSelectedLanguages } = combineAndDeduplicateLanguages(
      normalizedAnalyseLanguages,
      normalizedFreelanceLanguages
    )

    this.state = {
      loading: false,
      error: null,
      formData: {
        analyseData: {
          title: analyse.profile.title,
          summary: analyse.profile.summary,
        },
        freelanceData: {
          title: freelance.title,
          summary: freelance.summary,
        },
        combinedDiplomas: combinedDiplomas,
        combinedMissions: combinedMissions,
        combinedCertifications: combinedCertifications,
        combinedItSkills: combinedItSkills,
        combinedLanguages: combinedLanguages,
      },
      selected: {
        title: 'analyseData',
        summary: 'analyseData',
        diplomas: initialSelectedDiplomas,
        missions: initialSelectedMissions,
        certifications: initialSelectedCertifications,
        itSkills: initialSelectedItSkills,
        languages: initialSelectedLanguages,
      },
      isSectionOpen: {
        title: true,
        summary: false,
        missions: false,
        diplomas: false,
        certifications: false,
        itSkills: false,
        languages: false,
      },
      openDiplomas: {},
      openMissions: {},
      openCertifications: {},
      openItSkills: {},
      openLanguages: {},
      deletedFreelanceMissions: [],
    }
  }

  async componentDidMount() {
    const { formData } = this.state
    const newCombinedLanguages = [...formData.combinedLanguages]
    let changed = false

    for (let i = 0; i < newCombinedLanguages.length; i++) {
      const entry = newCombinedLanguages[i]
      if (entry.analyseData && !entry.analyseData.name) {
        try {
          const response = await api.languages.name({ languageId: entry.analyseData.languageId })
          if (response.data) {
            entry.analyseData.name = response.data
            if (entry.isNew) {
              entry.name = response.data
            }
            changed = true
          }
        } catch (e) {
          console.error(`Failed to fetch name for language ${entry.analyseData.languageId}`, e)
        }
      }
    }

    if (changed) {
      this.setState({
        formData: {
          ...formData,
          combinedLanguages: newCombinedLanguages
        }
      })
    }
  }

  handleDelete = (category, index) => {
    this.setState(prevState => {
      const fieldName = `combined${category.charAt(0).toUpperCase() + category.slice(1)}`
      const newCombined = [...prevState.formData[fieldName]]
      const itemToDelete = newCombined[index]

      let deletedFreelanceMissions = [...prevState.deletedFreelanceMissions]
      if (category === 'missions' && itemToDelete.freelanceData && itemToDelete.freelanceData._id) {
        deletedFreelanceMissions.push(itemToDelete.freelanceData._id)
      }

      newCombined.splice(index, 1)

      return {
        formData: {
          ...prevState.formData,
          [fieldName]: newCombined,
        },
        deletedFreelanceMissions
      }
    })
  }

  toggleSection = (section) => {
    this.setState(prevState => ({
      isSectionOpen: {
        ...prevState.isSectionOpen,
        [section]: !prevState.isSectionOpen[section],
      }
    }))
  }

  toggleDiploma = (diplomaId) => {
    this.setState(prevState => ({
      openDiplomas: {
        ...prevState.openDiplomas,
        [diplomaId]: !prevState.openDiplomas[diplomaId],
      }
    }))
  }

  toggleMission = (missionId) => {
    this.setState(prevState => ({
      openMissions: {
        ...prevState.openMissions,
        [missionId]: !prevState.openMissions[missionId],
      }
    }))
  }

  toggleCertification = (certificationId) => {
    this.setState(prevState => ({
      openCertifications: {
        ...prevState.openCertifications,
        [certificationId]: !prevState.openCertifications[certificationId],
      }
    }))
  }

  toggleItSkill = (itSkillId) => {
    this.setState(prevState => ({
      openItSkills: {
        ...prevState.openItSkills,
        [itSkillId]: !prevState.openItSkills[itSkillId],
      }
    }))
  }

  toggleLanguage = (langId) => {
    this.setState(prevState => ({
      openLanguages: {
        ...prevState.openLanguages,
        [langId]: !prevState.openLanguages[langId],
      }
    }))
  }

  static propTypes = {
    analyse: PropTypes.object.isRequired,
    freelance: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    onSave: PropTypes.func.isRequired,
    isRegistrationFlow: PropTypes.bool,
  }

  static defaultProps = {
    isRegistrationFlow: false,
  }

  handleSelectionChange = (category, id, source) => {
    this.setState(prevState => {
      const newSelected = { ...prevState.selected }

      if (category === 'diplomas') {
        newSelected.diplomas = {
          ...newSelected.diplomas,
          [id]: source,
        }
      } else if (category === 'missions') {
        newSelected.missions = {
          ...newSelected.missions,
          [id]: source,
        }
      } else if (category === 'certifications') {
        newSelected.certifications = {
          ...newSelected.certifications,
          [id]: source,
        }
      } else if (category === 'itSkills') {
        newSelected.itSkills = {
          ...newSelected.itSkills,
          [id]: source,
        }
      } else if (category === 'languages') {
        newSelected.languages = {
          ...newSelected.languages,
          [id]: source,
        }
      } else {
        newSelected[id] = source
      }

      return { selected: newSelected }
    })
  }

  handleInputChange = (source, field, value) => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [source]: {
          ...prevState.formData[source],
          [field]: value,
        },
      },
    }))
  }

  handleDiplomaInputChange = (index, source, field, value) => {
    this.setState(prevState => {
      const newCombinedDiplomas = [...prevState.formData.combinedDiplomas]

      if (!newCombinedDiplomas[index][source]) {
        newCombinedDiplomas[index][source] = {}
      }

      newCombinedDiplomas[index][source][field] = value

      return {
        formData: {
          ...prevState.formData,
          combinedDiplomas: newCombinedDiplomas,
        },
      }
    })
  }

  handleMissionInputChange = (index, source, field, value) => {
    this.setState(prevState => {
      const newCombinedMissions = [...prevState.formData.combinedMissions]

      if (!newCombinedMissions[index][source]) {
        newCombinedMissions[index][source] = {}
      }

      newCombinedMissions[index][source][field] = value

      return {
        formData: {
          ...prevState.formData,
          combinedMissions: newCombinedMissions,
        },
      }
    })
  }

  handleCertificationInputChange = (index, source, field, value) => {
    this.setState(prevState => {
      const newCombinedCertifications = [...prevState.formData.combinedCertifications]

      if (!newCombinedCertifications[index][source]) {
        newCombinedCertifications[index][source] = {}
      }

      newCombinedCertifications[index][source][field] = value

      return {
        formData: {
          ...prevState.formData,
          combinedCertifications: newCombinedCertifications,
        },
      }
    })
  }

  handleItSkillInputChange = (index, source, field, value) => {
    this.setState(prevState => {
      const newCombinedItSkills = [...prevState.formData.combinedItSkills]

      if (!newCombinedItSkills[index][source]) {
        newCombinedItSkills[index][source] = {}
      }

      newCombinedItSkills[index][source][field] = value

      return {
        formData: {
          ...prevState.formData,
          combinedItSkills: newCombinedItSkills,
        },
      }
    })
  }

  handleLanguageInputChange = (index, source, field, value) => {
    this.setState(prevState => {
      const newCombinedLanguages = [...prevState.formData.combinedLanguages]

      if (!newCombinedLanguages[index][source]) {
        newCombinedLanguages[index][source] = {}
      }

      newCombinedLanguages[index][source][field] = value

      return {
        formData: {
          ...prevState.formData,
          combinedLanguages: newCombinedLanguages,
        },
      }
    })
  }

  handleSave = async () => {
    this.setState({ loading: true, error: null })
    const { freelance, onSave } = this.props
    const { formData, selected } = this.state

    const payload = {
      freelance: freelance.id,
      diplomes: [],
      certifications: [],
      it_skills: [],
    }

    for (const fieldName in selected) {
      if (fieldName === 'diplomas' || fieldName === 'missions' || fieldName === 'certifications' || fieldName === 'itSkills' || fieldName === 'languages') continue

      if (Object.prototype.hasOwnProperty.call(selected, fieldName)) {
        const source = selected[fieldName]
        payload[fieldName] = formData[source][fieldName]
      }
    }

    payload.diplomes = formData.combinedDiplomas.map(combinedDiplomaEntry => {
      const selectedSource = selected.diplomas[combinedDiplomaEntry._id]
      let diplomaToSave = null

      if (selectedSource === 'analyseData' && combinedDiplomaEntry.analyseData) {
        diplomaToSave = combinedDiplomaEntry.analyseData
      } else if (selectedSource === 'freelanceData' && combinedDiplomaEntry.freelanceData) {
        diplomaToSave = combinedDiplomaEntry.freelanceData
      }

      if (diplomaToSave) {
        return {
          name: diplomaToSave.name,
          description: diplomaToSave.description,
          year: diplomaToSave.year
        }
      }

      return null
    }).filter(d => d !== null)

    payload.certifications = formData.combinedCertifications.map(combinedCertificationEntry => {
      const selectedSource = selected.certifications[combinedCertificationEntry._id]
      let certificationToSave = null

      if (selectedSource === 'analyseData' && combinedCertificationEntry.analyseData) {
        certificationToSave = combinedCertificationEntry.analyseData
      } else if (selectedSource === 'freelanceData' && combinedCertificationEntry.freelanceData) {
        certificationToSave = combinedCertificationEntry.freelanceData
      }

      if (certificationToSave) {
        return {
          label: certificationToSave.label,
          date: certificationToSave.date
        }
      }

      return null
    }).filter(c => c !== null)

    payload.it_skills = formData.combinedItSkills.map(combinedItSkillEntry => {
      const selectedSource = selected.itSkills[combinedItSkillEntry._id]
      let itSkillToSave = null

      if (selectedSource === 'analyseData' && combinedItSkillEntry.analyseData) {
        itSkillToSave = combinedItSkillEntry.analyseData
      } else if (selectedSource === 'freelanceData' && combinedItSkillEntry.freelanceData) {
        itSkillToSave = combinedItSkillEntry.freelanceData
      }

      if (itSkillToSave) {
        return {
          name: itSkillToSave.name
        }
      }

      return null
    }).filter(s => s !== null)

    try {
      for (const experienceId of this.state.deletedFreelanceMissions) {
        await api.freelances.experiences.delete(null, {
          freelance: freelance.id,
          experience: experienceId
        })
      }

      const missionsToAdd = formData.combinedMissions.filter(m =>
        selected.missions[m._id] === 'analyseData' && (m.isNew || m.isDifferent)
      )

      for (const mission of missionsToAdd) {
        if (mission.isDifferent && mission.freelanceData && mission.freelanceData._id) {
          await api.freelances.experiences.delete(null, {
            freelance: freelance.id,
            experience: mission.freelanceData._id
          })
        }

        const missionData = mission.analyseData

        await api.freelances.experiences.add({
          title: missionData.title,
          company: missionData.company,
          current: missionData.current,
          start: missionData.start,
          end: missionData.end,
          locality: missionData.locality || null,
          description: missionData.description,
        }, {
          freelance: freelance.id
        })
      }

      const identicalMissionsToUpdate = formData.combinedMissions.filter(m =>
        m.isIdentical && m.freelanceData && m.freelanceData._id && m.analyseData &&
        m.analyseData.description !== m.freelanceData.description
      )

      for (const mission of identicalMissionsToUpdate) {
        const missionData = mission.analyseData

        await api.freelances.experiences.edit({
          title: missionData.title,
          company: missionData.company,
          current: missionData.current,
          start: missionData.start,
          end: missionData.end,
          locality: missionData.locality || null,
          description: missionData.description,
        }, {
          freelance: freelance.id,
          experience: mission.freelanceData._id
        })
      }

      if (freelance.languages && freelance.languages.length > 0) {
        for (const lang of freelance.languages) {
          await api.freelanceLanguages.delete(null, {
            freelance: freelance.id,
            language: lang.language
          })
        }
      }

      const languagesToSave = formData.combinedLanguages.map(combinedLangEntry => {
        const selectedSource = selected.languages[combinedLangEntry._id]
        let langToSave = null

        if (selectedSource === 'analyseData' && combinedLangEntry.analyseData) {
          langToSave = combinedLangEntry.analyseData
        } else if (selectedSource === 'freelanceData' && combinedLangEntry.freelanceData) {
          langToSave = combinedLangEntry.freelanceData
        }

        return langToSave
      }).filter(l => l !== null && l.languageId)

      for (const lang of languagesToSave) {
        await api.freelanceLanguages.add({
          freelance: freelance.id,
          language: lang.languageId,
          level: lang.level
        }, {
          freelance: freelance.id,
          language: lang.languageId,
          level: lang.level
        })
      }

      const response = await api.freelances.edit(payload)
      toastr.success('Les données ont correctement été enregistrées')
      if (onSave) {
        onSave(response.data)
      }
    } catch (e) {
      console.error(e)
      this.setState({ loading: false, error: "Erreur lors de l'enregistrement des données" })
      toastr.error("Erreur lors de l'enregistrement des données")
    }
  }

  render() {
    const { analyse, freelance } = this.props
    const { loading, error, formData, selected, isSectionOpen, openDiplomas, openMissions, openCertifications, openItSkills, openLanguages } = this.state

    const freelanceTitleEmpty = !freelance.title || freelance.title.trim() === ''
    const analyseTitleEmpty = !analyse.profile.title || analyse.profile.title.trim() === ''
    const isNewTitle = freelanceTitleEmpty && !analyseTitleEmpty
    const isDifferentTitle = !isNewTitle && (freelance.title !== analyse.profile.title)

    const freelanceSummaryEmpty = !freelance.summary || freelance.summary.trim() === ''
    const analyseSummaryEmpty = !analyse.profile.summary || analyse.profile.summary.trim() === ''
    const isNewSummary = freelanceSummaryEmpty && !analyseSummaryEmpty
    const isDifferentSummary = !isNewSummary && (freelance.summary !== analyse.profile.summary)

    const isComparison = {
      title: {
        isNew: isNewTitle,
        isDifferent: isDifferentTitle,
      },
      summary: {
        isNew: isNewSummary,
        isDifferent: isDifferentSummary,
      },
    }

    return (
      <div className="cv-data-comparison">
        <h2 className="text-center">Comparaison des données du CV</h2>
        <p className="subtitle text-center mb-4">Choisissez les données que vous souhaitez conserver et enregistrez.</p>

        {error && <div className="alert alert-danger">{error}</div>}

        <form>
          <TitleSection
            isOpen={isSectionOpen.title}
            toggleSection={this.toggleSection}
            isNew={isComparison.title.isNew}
            isDifferent={isComparison.title.isDifferent}
            selected={selected.title}
            formData={formData}
            handleSelectionChange={this.handleSelectionChange}
            handleInputChange={this.handleInputChange}
            isRegistrationFlow={this.props.isRegistrationFlow}
          />

          <SummarySection
            isOpen={isSectionOpen.summary}
            toggleSection={this.toggleSection}
            isNew={isComparison.summary.isNew}
            isDifferent={isComparison.summary.isDifferent}
            selected={selected.summary}
            formData={formData}
            handleSelectionChange={this.handleSelectionChange}
            handleInputChange={this.handleInputChange}
            isRegistrationFlow={this.props.isRegistrationFlow}
          />

          <MissionsSection
            isOpen={isSectionOpen.missions}
            toggleSection={this.toggleSection}
            combinedMissions={formData.combinedMissions}
            openMissions={openMissions}
            toggleMission={this.toggleMission}
            selectedMissions={selected.missions}
            handleSelectionChange={this.handleSelectionChange}
            handleMissionInputChange={this.handleMissionInputChange}
            handleDelete={this.handleDelete}
            isRegistrationFlow={this.props.isRegistrationFlow}
            freelanceId={this.props.freelance.id}
          />

          <DiplomasSection
            isOpen={isSectionOpen.diplomas}
            toggleSection={this.toggleSection}
            combinedDiplomas={formData.combinedDiplomas}
            openDiplomas={openDiplomas}
            toggleDiploma={this.toggleDiploma}
            selectedDiplomas={selected.diplomas}
            handleSelectionChange={this.handleSelectionChange}
            handleDiplomaInputChange={this.handleDiplomaInputChange}
            handleDelete={this.handleDelete}
            isRegistrationFlow={this.props.isRegistrationFlow}
          />

          <CertificationsSection
            isOpen={isSectionOpen.certifications}
            toggleSection={this.toggleSection}
            combinedCertifications={formData.combinedCertifications}
            openCertifications={openCertifications}
            toggleCertification={this.toggleCertification}
            selectedCertifications={selected.certifications}
            handleSelectionChange={this.handleSelectionChange}
            handleCertificationInputChange={this.handleCertificationInputChange}
            handleDelete={this.handleDelete}
            isRegistrationFlow={this.props.isRegistrationFlow}
          />

          <ItSkillsSection
            isOpen={isSectionOpen.itSkills}
            toggleSection={this.toggleSection}
            combinedItSkills={formData.combinedItSkills}
            openItSkills={openItSkills}
            toggleItSkill={this.toggleItSkill}
            selectedItSkills={selected.itSkills}
            handleSelectionChange={this.handleSelectionChange}
            handleItSkillInputChange={this.handleItSkillInputChange}
            handleDelete={this.handleDelete}
            isRegistrationFlow={this.props.isRegistrationFlow}
          />

          <LanguagesSection
            isOpen={isSectionOpen.languages}
            toggleSection={this.toggleSection}
            combinedLanguages={formData.combinedLanguages}
            openLanguages={openLanguages}
            toggleLanguage={this.toggleLanguage}
            selectedLanguages={selected.languages}
            handleSelectionChange={this.handleSelectionChange}
            handleLanguageInputChange={this.handleLanguageInputChange}
            handleDelete={this.handleDelete}
            isRegistrationFlow={this.props.isRegistrationFlow}
          />

          <div className="mt-5 text-center">
            <button
              type="button"
              className="btn btn-primary"
              onClick={this.handleSave}
              disabled={loading}
            >
              Sauvegarder
            </button>
          </div>
        </form>

        <Loader visible={loading}>
          Sauvegarde en cours...
        </Loader>
      </div>
    )
  }
}
